import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'libs/styled';
import useBreakpoint from '@fogia/core-web/hooks/useBreakpoint';
import SliderHeader from 'components/sliders/SliderHeader';
import type { ContentSliderDesktopApi } from 'components/sliders/ContentSlider/types';
import type { ProductCardProduct } from 'components/product/ProductCard';
import ProductSliderDesktop, {
    productSliderFitsStaticGrid,
} from 'components/sliders/ProductSlider/ProductSliderDesktop';
import ProductScrollMobile from 'components/sliders/ProductSlider/ProductScrollMobile';
import {
    ProductSliderGroupNav,
    PRODUCT_SLIDER_HEADER_TYPOGRAPHY,
    type ProductSliderGroupItem,
} from 'components/sliders/ProductSlider/ProductSliderGroupNav';
import type { ButtonLinkComponent } from 'libs/sanity/types/generated';

export type { ProductSliderGroupItem };

/** One selectable group in the product slider (tab + product list). */
export type ProductSliderGroupWithProducts = {
    /**
     * Stable id for this group: React list keys, active-tab state, and persistence across data updates.
     * Must not change for the “same” group when the parent re-renders or refetches.
     */
    key: string;
    // Optional label in the group nav. When there is only one group, a non-empty title still shows the nav.
    groupTitle?: string;
    // Products for this group when it is active. Callers map upstream data to `ProductCardProduct` before passing it in.
    products: ProductCardProduct[];
};

const sliderContentForwardProps = {
    shouldForwardProp: (prop: string) => prop !== '$contentHeightPx',
};

/** Padding + height from measured inner. `overflow: visible` keeps ProductSliderCarousel horizontal bleed. */
const SliderContentWrapper = styled('div', sliderContentForwardProps)<{ $contentHeightPx: number | null }>`
    padding-left: var(--grid-padding, 10px);
    padding-right: var(--grid-padding, 10px);
    overflow: visible;
    height: ${({ $contentHeightPx }) => ($contentHeightPx == null ? 'auto' : `${$contentHeightPx}px`)};
    transition: height var(--transition-slider);
`;

export interface ProductSliderSectionProps {
    /** All selectable groups and their products; which group is active is internal state here. */
    groups: ProductSliderGroupWithProducts[];
    /** Optional header link (label comes from the link object). */
    link?: ButtonLinkComponent;
}

/** Conservative content height so scrollable overflow inside the measure root is included. */
const readContentHeightPx = (inner: HTMLDivElement): number =>
    Math.ceil(Math.max(inner.getBoundingClientRect().height, inner.scrollHeight, 0));

/** Strip product lists for the tab component—it only needs keys and titles. */
const toGroupNavItems = (groups: ProductSliderGroupWithProducts[]): ProductSliderGroupItem[] =>
    groups.map((group) => ({
        key: group.key,
        type: 'productSliderContentGroup' as const,
        groupTitle: group.groupTitle,
    }));

/**
 * Storefront product slider. The `groups` prop defines tabbed product sets supplied by the parent: each group has a label and its own products; the track shows only the active group's products.
 *
 * @remarks
 * - Parents build {@link ProductSliderGroupWithProducts} upstream (e.g. `ProductSliderModule`); this component does not fetch.
 * - Desktop: measured wrapper height + `ResizeObserver`, `height` transition via `var(--transition-slider)`.
 * - Returns `null` when no group has any products.
 */
const ProductSliderSection: React.FC<ProductSliderSectionProps> = ({ groups, link }) => {
    const isDesktop = useBreakpoint({ from: 'tablet.lg' });

    const [activeGroupKey, setActiveGroupKey] = useState<string | null>(() => groups[0]?.key ?? null);

    // When `groups` changes: keep the active key if it still exists, else the first—state may survive without remount (e.g. parent list keyed by index).
    useEffect(() => {
        if (groups.length === 0) {
            setActiveGroupKey(null);
            return;
        }
        setActiveGroupKey((previous) => {
            if (previous != null && groups.some((group) => group.key === previous)) {
                return previous;
            }
            return groups[0].key;
        });
    }, [groups]);

    // Product list for the body + header logic is always derived from the active group.
    const products = useMemo(() => {
        if (groups.length === 0 || activeGroupKey == null) {
            return [];
        }
        const active = groups.find((group) => group.key === activeGroupKey) ?? groups[0];
        return active.products;
    }, [groups, activeGroupKey]);

    const hasAnyProductsInGroups = useMemo(() => groups.some((group) => group.products.length > 0), [groups]);

    // Exposed by `ProductSliderDesktop` when it runs in carousel mode; header chevrons call into this API.
    const [navApi, setNavApi] = useState<ContentSliderDesktopApi | null>(null);
    const [contentHeightPx, setContentHeightPx] = useState<number | null>(null);
    const measureInnerRef = useRef<HTMLDivElement | null>(null);

    const handleSliderReady = useCallback((api: ContentSliderDesktopApi) => {
        setNavApi(api);
    }, []);

    // Few/narrow products fit a single-row 24-col grid; more/wider ones use the carousel (see desktop components).
    const shouldUseStaticGrid = useMemo(() => productSliderFitsStaticGrid(products), [products]);

    // Re-run height measurement when the visible product set changes (IDs), not only when array reference changes.
    const productIdsKey = useMemo(() => products.map((product) => product.objectID).join(','), [products]);

    // Carousel API is irrelevant on mobile or when the static grid is used—clear so chevrons do not call stale methods.
    useEffect(() => {
        if (!isDesktop || shouldUseStaticGrid) {
            setNavApi(null);
        }
    }, [isDesktop, shouldUseStaticGrid]);

    // Desktop: measure the inner product row and drive `SliderContentWrapper` height (+ CSS transition).
    useLayoutEffect(() => {
        if (!isDesktop) {
            return undefined;
        }

        const inner = measureInnerRef.current;
        if (!inner || typeof ResizeObserver === 'undefined') {
            return undefined;
        }

        const applyHeight = (): void => {
            const nextPx = readContentHeightPx(inner);
            if (nextPx > 0) {
                setContentHeightPx(nextPx);
            }
        };

        const resizeObserver = new ResizeObserver(applyHeight);
        resizeObserver.observe(inner);
        applyHeight();
        const frameId = requestAnimationFrame(applyHeight);

        return () => {
            cancelAnimationFrame(frameId);
            resizeObserver.disconnect();
        };
    }, [isDesktop, shouldUseStaticGrid, productIdsKey]);

    const groupNavItems = useMemo(() => toGroupNavItems(groups), [groups]);
    const hasGroupHeader = groups.length > 1 || (groups.length === 1 && Boolean(groups[0].groupTitle?.trim()));

    const leftSlot = useMemo(() => {
        if (!hasGroupHeader) {
            return undefined;
        }
        return (
            <ProductSliderGroupNav
                groups={groupNavItems}
                activeGroupKey={activeGroupKey}
                onSelectGroup={setActiveGroupKey}
            />
        );
    }, [groupNavItems, activeGroupKey, hasGroupHeader]);

    const headerSanityLink = link != null ? { sanityLink: link, label: link.label ?? '' } : undefined;

    if (!hasAnyProductsInGroups) {
        return null;
    }

    const showDesktopChevrons = isDesktop && !shouldUseStaticGrid && products.length > 1;

    return (
        <>
            <SliderHeader
                leftSlot={leftSlot}
                title=""
                titleLevel="h2"
                sanityLink={headerSanityLink}
                navApi={isDesktop ? navApi : undefined}
                showChevrons={showDesktopChevrons}
                linkTypography={PRODUCT_SLIDER_HEADER_TYPOGRAPHY}
            />
            {products.length > 0 ? (
                isDesktop ? (
                    <SliderContentWrapper $contentHeightPx={contentHeightPx}>
                        <div ref={measureInnerRef}>
                            <ProductSliderDesktop products={products} onReady={handleSliderReady} />
                        </div>
                    </SliderContentWrapper>
                ) : (
                    <ProductScrollMobile products={products} />
                )
            ) : null}
        </>
    );
};

export default ProductSliderSection;
